<template>
  <div class="plant-history-list">
    <div v-if="items.length == 0 && !loading" class="no-results">
      <span>No results found</span>
    </div>
    <ListView
      class="plant-history-list__view"
      :data-items="items"
      item="historyTile"
      footer="historyListFooter"
      @scroll="usePageScroll ? scrollHandler($event as unknown as Event) : null"
      data-cy="history-list"
    >
      <template v-slot:historyTile="{ props }">
        <PlantHistoryTile class="plant-history-list__tile" :item="props.dataItem" @itemClicked="onItemClicked" />
      </template>

      <template v-slot:historyListFooter>
        <Loader v-show="loading" class="history-loader" size="large" type="pulsing" data-cy="history-loader" />
      </template>
    </ListView>
  </div>
</template>

<script setup lang="ts">
import type { PlantHistoryDisplay } from "@/Domain/PlantHistoryDisplay";
import { onBeforeMount, onBeforeUnmount, ref, watchEffect } from "vue";
import { ListView } from "@progress/kendo-vue-listview";
import { Loader } from "@progress/kendo-vue-indicators";
import PlantHistoryTile from "./PlantHistoryTile.vue";

const props = defineProps<{
  items: PlantHistoryDisplay[];
  total: number;
  defaultTake?: number;
  skip: number;
  loading: boolean;
  usePageScroll?: boolean;
}>();

const emit = defineEmits<{
  (e: "itemsChanged", skip: number, take: number): void;
  (e: "itemClicked", itemId: PlantHistoryDisplay): void;
}>();

const take = ref<number>(props.defaultTake ?? 10);
const skip = ref<number>(props.skip);

const loadScrollThreshold = 100;

const scrollHandler = function (e: Event) {
  let target = e.target as HTMLDivElement | null;
  if (props.usePageScroll) target = document?.documentElement as HTMLDivElement | null;

  if (!target) return;
  if (target.scrollTop + loadScrollThreshold < target.scrollHeight - target.clientHeight) return;
  if (skip.value + take.value >= props.total) return;
  if (props.loading) return;

  skip.value = skip.value + take.value;
  emit("itemsChanged", skip.value, take.value);
};

const onItemClicked = function (item: PlantHistoryDisplay) {
  emit("itemClicked", item);
};

watchEffect(() => {
  skip.value = props.skip;
});

onBeforeMount(() => {
  if (props.usePageScroll) window.addEventListener("scroll", scrollHandler);
});

onBeforeUnmount(() => {
  if (props.usePageScroll) window.removeEventListener("scroll", scrollHandler);
});
</script>

<style lang="scss">
.plant-history-list {
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;

  &__view {
    background: none;
    border: none;
    overflow: auto;

    flex: 1 1 100%;

    .k-listview-content {
      padding-right: 8px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .k-listview-footer {
      border-top-width: 0;
    }

    &__tile {
      margin-bottom: 32px;
    }
  }

  .history-loader {
    display: flex;
    justify-content: center;
    margin: 32px 0;
  }
}
</style>
